/**
 * Perfil profissional: a página que outra pessoa vai ler.
 *
 * Duas coisas aqui têm consequência fora da tela, e por isso recebem tratamento
 * diferente do resto do formulário:
 *
 * **O endereço público.** Depois de publicado, ele já pode estar num cartão,
 * numa bio de rede social, numa conversa. A tela tranca o campo enquanto o
 * perfil está no ar e explica o caminho — despublicar, trocar, publicar de
 * novo — em vez de aceitar a troca e quebrar o link de quem divulgou.
 *
 * **A verificação.** É mostrada, nunca enviada. O campo não existe no corpo da
 * requisição, o servidor não o escreve, e o banco não concede a coluna. Aqui
 * ela é só informação de estado.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/Alert';
import { ChipGroup, OptionalMark } from '@/components/ChipGroup';
import {
  MODALITIES,
  SERVICE_MODES,
  type Modality,
  type ServiceMode,
} from '@/features/profiles/api';
import {
  ruleKey,
  useProfessionalProfile,
  useSaveProfessionalProfile,
} from '@/features/profiles/useProfiles';

const MIN_BIO = 40;
const MAX_BIO = 1200;

export default function ProfessionalProfilePage() {
  const { t, i18n } = useTranslation(['profiles', 'common']);
  const { data, isPending, isError } = useProfessionalProfile();
  const mutation = useSaveProfessionalProfile();

  const [slug, setSlug] = useState('');
  const [bio, setBio] = useState('');
  const [modalities, setModalities] = useState<Modality[]>([]);
  const [serviceModes, setServiceModes] = useState<ServiceMode[]>([]);
  const [city, setCity] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [registration, setRegistration] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  useEffect(() => {
    if (!data) return;
    setSlug(data.slug);
    setBio(data.bio ?? '');
    setModalities(data.modalities);
    setServiceModes(data.service_modes);
    setCity(data.city ?? '');
    setNeighborhood(data.neighborhood ?? '');
    setRegistration(data.registration_text ?? '');
    setIsPublic(data.is_public);
  }, [data]);

  if (isPending) {
    return (
      <div className="px-5 py-6" aria-busy="true">
        <p className="text-(--color-sand-700)">{t('common:state.loading')}</p>
      </div>
    );
  }

  const slugLocked = data?.can_change_slug === false;
  const bioLength = bio.trim().length;
  const canPublish = bioLength >= MIN_BIO;

  function handleSubmit(nextPublic = isPublic) {
    mutation.mutate({
      slug: slug.trim(),
      bio: bio.trim() || null,
      content_locale: data?.content_locale ?? i18n.language,
      modalities,
      service_modes: serviceModes,
      country_code: data?.country_code ?? null,
      city: city.trim() || null,
      neighborhood: neighborhood.trim() || null,
      registration_text: registration.trim() || null,
      is_public: nextPublic,
    });
  }

  return (
    <div className="flex flex-col gap-6 px-5 py-6">
      <header>
        <h1 className="text-3xl">{t('profiles:professional.title')}</h1>
        <p className="mt-1 text-(--color-sand-700)">{t('profiles:professional.subtitle')}</p>
      </header>

      {isError ? <Alert tone="error">{t('profiles:loadFailed')}</Alert> : null}

      <section className="mh-card">
        <p className="text-sm font-semibold text-(--color-sand-900)">
          {t('profiles:professional.status')}
        </p>
        <p className="mt-1 text-sm text-(--color-sand-700)">
          {t(`profiles:verification.${data?.verification_status ?? 'unverified'}`)}
        </p>
      </section>

      <div className="mh-field">
        <label htmlFor="slug">{t('profiles:professional.slug')}</label>
        <p className="mb-2 text-sm text-(--color-sand-700)">
          {slugLocked ? t('profiles:professional.slugLocked') : t('profiles:professional.slugHint')}
        </p>
        <input
          id="slug"
          type="text"
          inputMode="url"
          autoComplete="off"
          maxLength={80}
          disabled={slugLocked}
          value={slug}
          onChange={(event) => setSlug(event.target.value)}
        />
      </div>

      <div className="mh-field">
        <label htmlFor="bio">
          {t('profiles:professional.bio')}
          <OptionalMark label={t('profiles:optional')} />
        </label>
        <p className="mb-2 text-sm text-(--color-sand-700)">{t('profiles:professional.bioHint')}</p>
        <textarea
          id="bio"
          rows={5}
          maxLength={MAX_BIO}
          value={bio}
          onChange={(event) => setBio(event.target.value)}
        />
        {/* O aviso só aparece enquanto a bio é curta demais para publicar. Um
            contador permanente transforma escrever uma apresentação em cumprir
            uma cota — e diz o mínimo em vez de contar o que falta, para não
            mudar de número a cada tecla. */}
        {!canPublish ? (
          <p className="mt-1 text-sm text-(--color-sand-700)" data-numeric>
            {t('profiles:professional.bioMinimum', { min: MIN_BIO })}
          </p>
        ) : null}
      </div>

      <ChipGroup
        legend={t('profiles:professional.modalities')}
        optionalLabel={t('profiles:optional')}
        options={MODALITIES.map((value) => ({ value, label: t(`profiles:modality.${value}`) }))}
        selected={modalities}
        onChange={setModalities}
      />

      <ChipGroup
        legend={t('profiles:professional.serviceModes')}
        optionalLabel={t('profiles:optional')}
        options={SERVICE_MODES.map((value) => ({
          value,
          label: t(`profiles:serviceMode.${value}`),
        }))}
        selected={serviceModes}
        onChange={setServiceModes}
      />

      <div className="mh-field">
        <label htmlFor="city">
          {t('profiles:professional.city')}
          <OptionalMark label={t('profiles:optional')} />
        </label>
        <input
          id="city"
          type="text"
          maxLength={80}
          value={city}
          onChange={(event) => setCity(event.target.value)}
        />
      </div>

      <div className="mh-field">
        <label htmlFor="neighborhood">
          {t('profiles:professional.neighborhood')}
          <OptionalMark label={t('profiles:optional')} />
        </label>
        <input
          id="neighborhood"
          type="text"
          maxLength={80}
          value={neighborhood}
          onChange={(event) => setNeighborhood(event.target.value)}
        />
      </div>

      <div className="mh-field">
        <label htmlFor="registration">
          {t('profiles:professional.registration')}
          <OptionalMark label={t('profiles:optional')} />
        </label>
        <p className="mb-2 text-sm text-(--color-sand-700)">
          {t('profiles:professional.registrationHint')}
        </p>
        <input
          id="registration"
          type="text"
          maxLength={60}
          value={registration}
          onChange={(event) => setRegistration(event.target.value)}
        />
      </div>

      {mutation.isError ? (
        <Alert tone="error" live="assertive">
          {t(ruleKey(mutation.error))}
        </Alert>
      ) : null}

      {mutation.isSuccess ? <Alert tone="success">{t('profiles:saved')}</Alert> : null}

      <button
        type="button"
        className="mh-button mh-button--primary w-full"
        disabled={mutation.isPending || slug.trim().length === 0}
        onClick={() => handleSubmit()}
      >
        {mutation.isPending ? t('common:state.loading') : t('profiles:save')}
      </button>

      <section className="mh-card">
        <p className="text-sm font-semibold text-(--color-sand-900)">
          {isPublic
            ? t('profiles:professional.publishedTitle')
            : t('profiles:professional.draftTitle')}
        </p>
        <p className="mt-1 text-sm text-(--color-sand-700)">
          {isPublic
            ? t('profiles:professional.publishedHint')
            : t('profiles:professional.draftHint')}
        </p>

        <button
          type="button"
          className="mh-button mh-button--secondary mt-4 w-full"
          disabled={mutation.isPending || (!isPublic && !canPublish)}
          onClick={() => {
            const next = !isPublic;
            setIsPublic(next);
            handleSubmit(next);
          }}
        >
          {isPublic ? t('profiles:professional.unpublish') : t('profiles:professional.publish')}
        </button>

        {!isPublic && !canPublish ? (
          <p className="mt-2 text-center text-sm text-(--color-sand-700)">
            {t('profiles:rule.bio_required_to_publish')}
          </p>
        ) : null}
      </section>
    </div>
  );
}
